/**
 * What each benchmark figure means, as hover content.
 *
 * This replaces a four-line paragraph that sat above the cards. The paragraph
 * was accurate but permanent: everyone read it once, nobody read it again, and
 * it pushed the numbers down the page. The questions it answered ("what is
 * Gain measured from?") are asked while looking at a specific figure, so the
 * answer belongs on that figure.
 *
 * Your row and a benchmark row are NOT the same measurement and must not share
 * wording: yours is a real account with deposits stripped out, a benchmark is a
 * hypothetical buy-and-hold of the same starting balance. Saying "Gain" for
 * both without qualifying is how a client concludes they underperformed a fund
 * they never held.
 */

/** Per-metric explanation for the "You" card — a real account. */
export const YOU_METRIC_HELP = {
  gain: 'Percent return since this account was first funded. Deposits and withdrawals are excluded, so it reflects trading, not funding.',
  monthly: 'Return over the trailing 30 days.',
  yearly: 'Return over the trailing 365 days. Shows "—" when the account is younger than that.',
  drawdown: 'Largest peak-to-trough fall in account value over the period.',
  profit: 'Money made or lost since the account was first funded, after commissions. Deposits are not counted as profit.',
};

/** Per-metric explanation for a benchmark card — a modeled buy-and-hold. */
export const BENCHMARK_METRIC_HELP = {
  gain: 'What a buy-and-hold of this benchmark would have returned over the same window, starting from your starting balance.',
  monthly: "The benchmark's return over the trailing 30 days.",
  yearly: "The benchmark's return over the trailing 365 days.",
  drawdown: "Largest peak-to-trough fall in the benchmark's price over the period.",
  profit: 'Money that same starting balance would have made or lost held in this benchmark. Hypothetical — not money you invested.',
};

/** Footnote shown under every benchmark card. */
export const BENCHMARK_FOOTNOTE = 'Price return only — dividends and distributions are excluded, '
  + 'so a dividend-paying benchmark is understated against its true total return.';

/** Footnote shown under the You card. */
export const YOU_FOOTNOTE = 'Measured since this account was first funded, over the date range selected above.';

/**
 * Build the hover content for one card.
 *
 * @param {{isYou?: boolean, label?: string, subtitle?: string, metrics?: object, rows?: Array}} input
 * @returns {{title: string, subtitle: string|null, rows: Array, footnote: string}}
 */
export function buildBenchmarkHelp({
  isYou = false,
  label = '',
  subtitle = null,
  rows = [],
} = {}) {
  const help = isYou ? YOU_METRIC_HELP : BENCHMARK_METRIC_HELP;
  return {
    title: label,
    subtitle,
    rows: rows
      .filter((row) => help[row.id])
      .map((row) => ({ id: row.id, label: row.label, help: help[row.id] })),
    footnote: isYou ? YOU_FOOTNOTE : BENCHMARK_FOOTNOTE,
  };
}
